import { useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";

import HomeCard3Sequence from "./interaction/interaction3";
import HomeCard4_ElbowDashed from "./interaction/intreaction4";
import HomeHubCards from "./interaction/interaction1";
import HomeCard2Sequence from "./interaction/interaction2";

import { useResponsiveMobile } from "../../../hooks/useResponsiveMobile";
import { Link } from "react-router-dom";

const MobileReveal = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const isMobile = useResponsiveMobile(900);
  if (!isMobile) return <>{children}</>;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.35 }}
      transition={{ duration: 0.6, ease: "easeOut", delay }}
    >
      {children}
    </motion.div>
  );
};

export const Section2 = () => {
  const isMobile = useResponsiveMobile(900);
  //  header만 뷰포트 진입 시 애니메이션
  const headerRef = useRef<HTMLElement | null>(null);
  const inView = useInView(headerRef, {
    // 화면 중간쯤 왔을 때 트리거
    amount: 0.5,
    // 1번만 실행
    once: true,
    // 위/아래 여백으로 트리거 시점 미세 조정
    margin: "0px 0px -10% 0px",
  });

  return (
    <section className="flex flex-col gap-[120px] items-center pt-[80px]">
      {/* section header */}
      <motion.header
        ref={headerRef}
        className="flex flex-col gap-6"
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{
          once: true,
          amount: 0.6, // 화면 60% 들어왔을 때 재생
        }}
        transition={{
          duration: 3, //  충분히 긴 시간
          ease: [0.12, 0.0, 0.25, 1], //  초반 거의 멈춤
        }}
      >
        <span className="font-normal text-[#0079FF] text-xl text-center">품에 대해 알아보기</span>
        <h2 className="font-bold text-[#121319] text-[36px] md:text-display text-center">
          품이 비즈니스 성장의 <br /> 다음 단계로 안내합니다!
        </h2>
      </motion.header>

      {/* value list */}
      <div className="flex flex-col md:gap-[100px] gap-[50px] items-center justify-center w-full">
        <MobileReveal delay={0.05}>
          {/* value item 1 */}
          <div className="flex md:gap-[60px] xl:gap-[100px] lg:gap-[80px] items-center w-full md:max-w-[1200px] lg:max-w-[1400px] md:px-[24px]">
            <div className="flex flex-col gap-[30px] w-full md:w-1/2 ">
              <div className="flex flex-col gap-[32px] w-full justify-center items-center md:items-start">
                <img
                  loading="lazy"
                  className="w-[36px] md:w-[56px]"
                  src="/assets/images/icon/value1.png"
                />

                <span className="font-normal text-base text-[#0079FF] text-center md:text-start">
                  핵심가치 1
                </span>
                <h3 className="font-bold text-[28px] md:text-display1_2 text-[#121319] text-center md:text-start">
                  복잡한 <span className="text-[#0079FF]">정산,</span> <br />
                  이제는 <span className="text-[#0079FF]">자동화</span>하세요
                </h3>
              </div>

              <span className="font-normal text-[#4D4F58] text-heading1 text-center md:text-start">
                #정산자동화 #통합데이터 #실수제로 #빠른정산
              </span>

              <p className="font-normal text-[#4D4F58] text-heading3 text-center md:text-start">
                흩어진 정산 데이터를 자동으로 <br /> 취합부터 대사까지 한 번에 끝내세요.
              </p>

              <Link
                className="text-center md:text-start font-normal text-[#0079FF] text-xl"
                to="/features#payout-service"
              >
                자세히 보러가기&gt;
              </Link>

              {isMobile ? (
                <div style={{ padding: "0 16px" }}>
                  <img
                    loading="lazy"
                    src="/assets/images/home/introduction1.png"
                    alt="eg1"
                    className="w-full h-auto max-w-[520px]"
                  />
                </div>
              ) : (
                ""
              )}
            </div>
            <div className="flex md:w-1/2">{isMobile ? "" : <HomeHubCards />}</div>
          </div>
        </MobileReveal>

        {/* value item 2 */}
        <MobileReveal delay={0.05}>
          <div className="flex md:gap-[60px] xl:gap-[100px] lg:gap-[80px] items-center w-full md:max-w-[1200px] lg:max-w-[1400px] px-[16px]">
            <div className="flex md:w-1/2">{isMobile ? "" : <HomeCard2Sequence />}</div>

            <div className="flex flex-col gap-[30px] md:w-1/2 w-full">
              <div className="flex flex-col gap-[32px] items-center md:items-start">
                <img
                  loading="lazy"
                  className="w-[36px] md:w-[56px]"
                  src="/assets/images/icon/value2.png"
                />

                <span className="font-normal text-base text-[#0079FF] text-center md:text-start">
                  핵심가치 2
                </span>

                <h3 className="font-bold text-[28px] md:text-display1_2 text-center md:text-start">
                  늘어나는 <span className="text-[#0079FF]">거래처,</span> <br />
                  제대로 <span className="text-[#0079FF]">관리</span>가 됩니다
                </h3>
              </div>

              <span className="font-normal text-[#4D4F58] text-heading1 text-center md:text-start">
                #영업대행관리 #파트너관리 #성과한눈에
              </span>

              <p className="font-normal text-[#4D4F58] text-heading3 text-center md:text-start">
                거래처·파트너 실적을 한 화면에서 효율적으로 관리하고 <br />
                당신의 비즈니스 성장에 유연하게 대처하세요.
              </p>
              <Link
                className="text-center md:text-start font-normal text-[#0079FF] text-xl"
                to="/features#sales-network"
              >
                자세히 보러가기&gt;
              </Link>
              {isMobile ? (
                <img
                  loading="lazy"
                  src="/assets/images/home/introduction2.png"
                  alt="eg1"
                  className="w-full h-auto max-w-[520px]"
                />
              ) : (
                ""
              )}
            </div>
          </div>
        </MobileReveal>

        {/* value item 3 */}
        <MobileReveal delay={0.05}>
          <div className="flex md:gap-[60px] xl:gap-[100px] lg:gap-[80px] items-center w-full md:max-w-[1200px] lg:max-w-[1400px] px-[16px]">
            <div className="flex flex-col gap-[30px] w-full md:w-1/2 ">
              <div className="flex flex-col gap-[32px] w-full justify-center items-center md:items-start">
                <img
                  loading="lazy"
                  className="w-[36px] md:w-[56px]"
                  src="/assets/images/icon/value3.png"
                />

                <span className="font-normal text-base text-[#0079FF] text-center md:text-start">
                  핵심가치 3
                </span>

                <h3 className="font-bold text-[28px] md:text-display1_2 text-[#121319] text-center md:text-start">
                  복잡한 <span className="text-[#0079FF]">개발 없이,</span> <br />
                  바로 쓰는 <span className="text-[#0079FF]">운영 시스템</span>
                </h3>
              </div>

              <span className="font-normal text-[#4D4F58] text-heading1 text-center md:text-start">
                #빠른도입 #운영시스템 #비용절감
              </span>

              <p className="font-normal text-[#4D4F58] text-heading3 text-center md:text-start">
                비용·시간 부담 없이 <br />
                필요한 시스템을 바로 갖추고 비즈니스를 바로 시작해보세요!
              </p>

              {isMobile ? (
                <img
                  loading="lazy"
                  src="/assets/images/home/introduction3.png"
                  alt="eg3"
                  className="w-full h-auto max-w-[520px]"
                />
              ) : (
                ""
              )}
            </div>
            <div className="flex md:w-1/2">{isMobile ? "" : <HomeCard3Sequence />}</div>
          </div>
        </MobileReveal>

        {/* value item 4 */}
        <MobileReveal delay={0.05}>
          <div className="flex md:gap-[60px] xl:gap-[100px] lg:gap-[80px] items-center w-full md:max-w-[1200px] lg:max-w-[1400px] px-[16px]">
            <div className="flex md:w-1/2">
              {isMobile ? (
                ""
              ) : (
                // <img
                //   src="/assets/images/home/introduction4.svg"
                //   alt="eg4"
                //   className="w-full h-auto max-w-[520px]"
                // />
                <HomeCard4_ElbowDashed />
              )}
            </div>

            <div className="flex flex-col gap-[30px] md:w-1/2 w-full">
              <div className="flex flex-col gap-[32px] items-center md:items-start">
                <img
                  loading="lazy"
                  className="w-[36px] md:w-[56px]"
                  src="/assets/images/icon/value4.png"
                />

                <span className="font-normal text-base text-[#0079FF] text-center md:text-start">
                  핵심가치 4
                </span>

                <h3 className="font-bold text-[28px] md:text-display1_2 text-center md:text-start">
                  멈춰있던 정산금이 흐르면, <br />
                  <span className="text-[#0079FF]">비즈니스 기회</span>가 보입니다
                </h3>
              </div>

              <span className="font-normal text-[#4D4F58] text-heading1 text-center md:text-start">
                #자금회전 #정산주기단축 #성장지원 {isMobile && <br />} #가맹점상생
              </span>

              <p className="font-normal text-[#4D4F58] text-heading3 text-center md:text-start">
                정산 대기 시간을 줄여 확보한 자금 흐름을 바탕으로 <br />
                신규 창업 지원 등 브랜드의 신뢰도와 기업 운영의 {isMobile && <br />}유동성을
                높이세요.
              </p>
              <Link
                className="text-center md:text-start font-normal text-[#0079FF] text-xl"
                to="/features#payout-service"
              >
                자세히 보러가기&gt;
              </Link>
              {isMobile ? (
                <img
                  loading="lazy"
                  src="/assets/images/home/introduction4.png"
                  alt="eg4"
                  className="w-full h-auto max-w-[520px]"
                />
              ) : (
                ""
              )}
            </div>
          </div>
        </MobileReveal>
      </div>
    </section>
  );
};
